import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { loginEmail, registerEmail, startGoogleLogin } from "@/lib/api";
import { Loader2 } from "lucide-react";

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (!d) return "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return String(d);
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.6 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16 4 9.1 8.6 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.3 0 10.1-2 13.7-5.3l-6.3-5.3C29.2 34.9 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.9l-6.5 5C9 39.4 15.9 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4-4 5.4l6.3 5.3C41.1 36.5 44 30.9 44 24c0-1.2-.1-2.3-.4-3.5z"/>
  </svg>
);

export const AuthDialog = () => {
  const { authOpen, closeAuth, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("signin"); // signin | signup
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setMsg("");
    setBusy(true);
    try {
      const u = mode === "signup"
        ? await registerEmail({ name: form.name, email: form.email, password: form.password })
        : await loginEmail({ email: form.email, password: form.password });
      setUser(u);
      toast.success(mode === "signup" ? "Welcome to Spherio Living!" : "Welcome back.");
      closeAuth();
      navigate(u.role === "admin" ? "/admin" : "/account", { replace: false });
    } catch (err) {
      setMsg(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "h-11 rounded-xl";

  return (
    <Dialog open={authOpen} onOpenChange={(o) => (o ? null : closeAuth())}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden gap-0"
        data-testid="auth-dialog"
      >
        <div className="p-8 sm:p-10">
          <DialogTitle className="font-head text-2xl sm:text-3xl font-medium text-[#1C1A17]">
            {mode === "signup" ? "Create your account" : "Sign in"}
          </DialogTitle>
          <DialogDescription className="text-[#5C574F] text-sm mt-2">
            Track your consultations, saved quotes and project milestones.
          </DialogDescription>

          {msg && (
            <div
              className="mt-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              data-testid="auth-error"
            >
              {msg}
            </div>
          )}

          {/* Social */}
          <div className="mt-6">
            <button
              type="button"
              onClick={startGoogleLogin}
              data-testid="auth-google-btn"
              className="w-full inline-flex items-center justify-center gap-3 rounded-xl border border-black/15 h-11 text-sm font-semibold text-[#0A0A0A] hover:border-[#1C1A17] transition-colors"
            >
              <GoogleIcon /> Continue with Google
            </button>
          </div>

          {/* Divider */}
          <div className="my-6 flex items-center gap-3">
            <span className="h-px flex-1 bg-black/10" />
            <span className="text-[11px] tracking-[0.2em] uppercase text-[#5C574F]">Or with email</span>
            <span className="h-px flex-1 bg-black/10" />
          </div>

          {/* Email form */}
          <form onSubmit={submit} className="space-y-4" data-testid="auth-email-form">
            {mode === "signup" && (
              <div className="space-y-1.5">
                <Label htmlFor="auth-name" className="text-[#1C1A17]">Full name</Label>
                <Input
                  id="auth-name"
                  data-testid="auth-name"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Jane Doe"
                  className={inputCls}
                  autoComplete="name"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="auth-email" className="text-[#1C1A17]">Email</Label>
              <Input
                id="auth-email"
                data-testid="auth-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="you@email.com"
                className={inputCls}
                autoComplete="email"
                required
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="auth-password" className="text-[#1C1A17]">Password</Label>
                {mode === "signin" && (
                  <Link
                    to="/forgot-password"
                    onClick={closeAuth}
                    data-testid="auth-forgot-link"
                    className="text-xs text-[#5C574F] hover:text-[#1C1A17]"
                  >
                    Forgot?
                  </Link>
                )}
              </div>
              <Input
                id="auth-password"
                data-testid="auth-password"
                type="password"
                value={form.password}
                onChange={set("password")}
                placeholder="••••••••"
                className={inputCls}
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                required
              />
            </div>

            <button
              type="submit"
              disabled={busy}
              data-testid="auth-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#B8935A] text-white h-11 text-sm font-semibold hover:bg-[#9C7A44] transition-colors disabled:opacity-70"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "signup" ? "Create account" : "Sign in"}
            </button>

            <div className="text-center text-sm text-[#5C574F]">
              {mode === "signup" ? "Already have an account?" : "New to Spherio Living?"}{" "}
              <button
                type="button"
                data-testid="auth-toggle-mode"
                onClick={() => {
                  setMode(mode === "signup" ? "signin" : "signup");
                  setMsg("");
                }}
                className="text-[#1C1A17] hover:text-[#B8935A] font-semibold"
              >
                {mode === "signup" ? "Sign in" : "Sign up"}
              </button>
            </div>
          </form>
        </div>

        {/* Legal footer */}
        <div className="bg-[#F5F1EA] border-t border-black/5 px-8 sm:px-10 py-5 text-xs leading-relaxed text-[#5C574F]" data-testid="auth-legal-footer">
          By continuing, you agree to our{" "}
          <Link
            to="/terms"
            onClick={closeAuth}
            data-testid="auth-terms-link"
            className="text-[#1C1A17] underline underline-offset-4 hover:text-[#B8935A]"
          >
            Terms &amp; Conditions
          </Link>{" "}
          and acknowledge our{" "}
          <Link
            to="/privacy-policy"
            onClick={closeAuth}
            data-testid="auth-privacy-link"
            className="text-[#1C1A17] underline underline-offset-4 hover:text-[#B8935A]"
          >
            Privacy Policy
          </Link>
          .
        </div>
      </DialogContent>
    </Dialog>
  );
};
